import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { subscribeVoiceInputEnergy, subscribeVoiceOutputEnergy } from '../../lib/voiceEnergyBus';
import { createOrbFrameScheduler } from '../../lib/voiceAnimationFrames';

const TAU = Math.PI * 2;

const STATE_TARGETS = {
  idle: { presence: 0.34, focus: 0.18, orbit: 0.12, emission: 0.08, warmth: 0.42, tension: 0.08 },
  muted: { presence: 0.24, focus: 0.12, orbit: 0.06, emission: 0.03, warmth: 0.2, tension: 0.06 },
  listening: { presence: 0.72, focus: 0.86, orbit: 0.34, emission: 0.18, warmth: 0.58, tension: 0.42 },
  transcribing: { presence: 0.68, focus: 0.48, orbit: 0.92, emission: 0.3, warmth: 0.56, tension: 0.36 },
  sending: { presence: 0.7, focus: 0.44, orbit: 1, emission: 0.34, warmth: 0.62, tension: 0.4 },
  speaking: { presence: 0.86, focus: 0.32, orbit: 0.56, emission: 0.88, warmth: 0.82, tension: 0.24 },
  error: { presence: 0.54, focus: 0.24, orbit: 0.2, emission: 0.18, warmth: 0.22, tension: 0.86 },
};

function clamp(value, min = 0, max = 1) {
  return Math.max(min, Math.min(max, value));
}

function mix(a, b, amount) {
  return a + (b - a) * amount;
}

function rgba(red, green, blue, alpha) {
  return `rgba(${Math.round(red)},${Math.round(green)},${Math.round(blue)},${clamp(alpha)})`;
}

// The shared microphone analyser has already removed its noise floor. Keep a
// small second-stage hysteresis here for room noise, but do not discard normal
// speaking energy before it reaches the material boundary.
const SPEECH_ON_THRESHOLD = 0.085;
const SPEECH_OFF_THRESHOLD = 0.04;
// Below half of one physical pixel, the rendered contour is visually
// indistinguishable from its circular resting boundary. This threshold affects
// only the latched testability witness; it does not alter canvas geometry.
const MIN_VISIBLE_CONTOUR_DISPLACEMENT_DEVICE_PX = 0.5;

export function speechGatedInputEnergy({ speechActive = false, rawEnergy = 0 }) {
  if (!speechActive) return 0;
  // Browser/VAD speech ownership is the gate. Preserve a small visible floor
  // for quiet intentional speech while leaving microphone energy in charge of
  // the deformation depth above that floor.
  return Math.max(0.055, clamp(rawEnergy) * 0.72);
}

export function advanceSpeechBoundary({
  rawEnergy = 0,
  elapsed = 1 / 60,
  active = false,
  gateCharge = 0,
  envelope = 0,
}) {
  const raw = clamp(rawEnergy);
  const delta = clamp(elapsed, 0.001, 0.05);
  let nextCharge = gateCharge;
  if (raw >= SPEECH_ON_THRESHOLD) nextCharge += delta * 18;
  else if (raw <= SPEECH_OFF_THRESHOLD) nextCharge -= delta * 9;
  else nextCharge -= delta * 1.8;
  nextCharge = clamp(nextCharge);

  const nextActive = active ? nextCharge > 0.12 : nextCharge >= 0.72;
  const normalized = nextActive
    ? clamp((raw - SPEECH_OFF_THRESHOLD) / (0.48 - SPEECH_OFF_THRESHOLD))
    : 0;
  const target = nextActive && raw > SPEECH_OFF_THRESHOLD
    ? 0.18 + Math.pow(normalized, 0.82) * 0.5
    : 0;
  const response = target > envelope ? 4.8 : 6.2;
  const nextEnvelope = mix(envelope, target, 1 - Math.exp(-delta * response));

  return {
    active: nextActive,
    gateCharge: nextCharge,
    envelope: nextEnvelope < 0.002 ? 0 : clamp(nextEnvelope),
    normalized,
  };
}

// A content-free, latched witness for completed canvas work. Unlike the
// per-frame envelope diagnostics, these counters remain observable after a
// transient speech frame has passed. Call this only after the material path
// has actually been drawn so it cannot self-certify from input energy alone.
export function advanceSpeechRenderWitness({
  cycle = 0,
  settledCycle = 0,
  reactiveRendered = false,
  reactiveGeometryRendered = false,
}) {
  if (reactiveGeometryRendered && !reactiveRendered) {
    return {
      cycle: cycle + 1,
      settledCycle,
      reactiveRendered: true,
    };
  }
  if (!reactiveGeometryRendered && reactiveRendered) {
    return {
      cycle,
      settledCycle: cycle,
      reactiveRendered: false,
    };
  }
  return { cycle, settledCycle, reactiveRendered };
}

export function voiceContourDisplacement({ angle, radius, time, energy, phase = 0 }) {
  const strength = clamp(energy);
  if (strength === 0) return 0;

  // Independent, non-linearly evolving phases keep the mixed waves changing
  // shape rather than rotating one fixed scalloped profile around the orb.
  const broadPhase = phase + Math.sin(time * 0.37 + 0.4) * 0.86 + time * 0.11;
  const mediumPhase = phase * 1.73 - Math.sin(time * 0.29 + 1.7) * 1.04 - time * 0.07;
  const detailPhase = phase * 2.31 + Math.sin(time * 0.43 + 2.2) * 0.72 + time * 0.05;
  const localDepth = 0.7 + Math.sin(angle * 2 - time * 0.23 + phase) * 0.18
    + Math.sin(angle * 4 + time * 0.17 + 1.1) * 0.12;
  const broadWave = Math.sin(angle * 3 + broadPhase) * 0.014 * localDepth;
  const crossingWave = Math.sin(angle * 6 + mediumPhase) * 0.022
    * (0.7 + Math.sin(angle - time * 0.19) * 0.3);
  const fineWave = Math.sin(angle * 11 + detailPhase) * 0.009
    * (0.66 + Math.sin(angle * 3 + time * 0.13) * 0.34);
  const displacement = radius * strength * (broadWave + crossingWave + fineWave);
  return clamp(displacement, radius * -0.04, radius * 0.042);
}

function traceOrbMaterialBoundary(context, cx, cy, radius, time, energy, phase = 0) {
  const points = 192;
  let maximumDisplacement = 0;
  context.beginPath();
  for (let index = 0; index <= points; index += 1) {
    const angle = (index / points) * TAU;
    const r = radius + voiceContourDisplacement({ angle, radius, time, energy, phase });
    maximumDisplacement = Math.max(maximumDisplacement, Math.abs(r - radius));
    const x = cx + Math.cos(angle) * r;
    const y = cy + Math.sin(angle) * r;
    if (index === 0) context.moveTo(x, y);
    else context.lineTo(x, y);
  }
  context.closePath();
  return maximumDisplacement;
}

export default function VoiceOrb({
  state = 'idle',
  inputEnergy = null,
  micMuted = false,
  speechActive = false,
}) {
  const canvasRef = useRef(null);
  const propsRef = useRef({ state, explicitInputEnergy: inputEnergy, busInputEnergy: 0, busOutputEnergy: 0, micMuted, speechActive });

  // Synchronize the canvas target before the browser paints the matching CSS
  // ring state. Updating after paint leaves one frame where the rings have
  // entered SPEAKING while the material body still renders THINKING/SENDING,
  // which presents as a flash or a different orb at the transition.
  useLayoutEffect(() => {
    propsRef.current.state = state;
    propsRef.current.explicitInputEnergy = inputEnergy == null ? null : clamp(Number(inputEnergy) || 0);
    propsRef.current.micMuted = micMuted;
    propsRef.current.speechActive = Boolean(speechActive);
  }, [state, inputEnergy, micMuted, speechActive]);

  useEffect(() => subscribeVoiceInputEnergy((busInputEnergy) => {
    propsRef.current.busInputEnergy = busInputEnergy;
  }), []);

  useEffect(() => subscribeVoiceOutputEnergy((busOutputEnergy) => {
    propsRef.current.busOutputEnergy = busOutputEnergy;
  }), []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || ''))) return undefined;

    const context = canvas.getContext?.('2d', { alpha: true });
    if (!context) return undefined;

    let frame = null;
    const frameScheduler = createOrbFrameScheduler();
    let lastTime = performance.now();
    let width = 0;
    let height = 0;
    let dpr = 1;
    let reduceMotion = false;
    let speechBoundary = { active: false, gateCharge: 0, envelope: 0, normalized: 0 };
    let speechRenderWitness = { cycle: 0, settledCycle: 0, reactiveRendered: false };
    let lastDiagnosticTime = 0;
    let canvasReady = false;
    const current = { ...(STATE_TARGETS[state] || STATE_TARGETS.idle) };

    const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const updateMotionPreference = () => { reduceMotion = Boolean(motionQuery?.matches); };
    updateMotionPreference();
    motionQuery?.addEventListener?.('change', updateMotionPreference);

    const motes = Array.from({ length: 22 }, (_, index) => ({
      angle: (index / 22) * TAU + (index % 3) * 0.17,
      radius: 0.2 + ((index * 37) % 71) / 100,
      size: 0.55 + ((index * 19) % 17) / 20,
      speed: 0.08 + ((index * 11) % 9) / 85,
      depth: 0.35 + ((index * 13) % 41) / 60,
    }));

    const layoutHost = canvas.parentElement;
    const resize = () => {
      // Measure the untransformed layout host, never the animated canvas.
      // Real iOS Safari can return compositor-adjusted canvas bounds and then
      // offset or progressively shrink the backing store when its intrinsic
      // size is mutated from the same ResizeObserver callback.
      const measuredWidth = layoutHost?.clientWidth || canvas.clientWidth || 0;
      const measuredHeight = layoutHost?.clientHeight || canvas.clientHeight || 0;
      // Mobile browser chrome, permission banners and orientation changes can
      // briefly report a zero-sized host. Never replace the last good backing
      // store with a 1x1 canvas during that transient layout frame; WebKit may
      // not deliver another ResizeObserver callback after the chrome settles.
      if (measuredWidth < 96 || measuredHeight < 96) return false;
      width = measuredWidth;
      height = measuredHeight;
      dpr = Math.min(window.devicePixelRatio || 1, 1.75);
      const backingWidth = Math.round(width * dpr);
      const backingHeight = Math.round(height * dpr);
      if (canvas.width !== backingWidth) canvas.width = backingWidth;
      if (canvas.height !== backingHeight) canvas.height = backingHeight;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      return true;
    };

    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(resize) : null;
    if (layoutHost) resizeObserver?.observe(layoutHost);
    window.addEventListener('resize', resize);
    window.addEventListener('orientationchange', resize);
    window.addEventListener('pageshow', resize);
    window.visualViewport?.addEventListener?.('resize', resize);
    resize();

    const render = (now) => {
      const elapsed = Math.min(0.05, Math.max(0.001, (now - lastTime) / 1000));
      lastTime = now;
      const seconds = now / 1000;
      const activeState = propsRef.current.state;
      if (width < 96 || height < 96) {
        resize();
        frame = frameScheduler.request(render);
        return;
      }
      const microphoneMuted = propsRef.current.micMuted;
      const stateTarget = STATE_TARGETS[activeState] || STATE_TARGETS.idle;
      const mutedInfluence = microphoneMuted ? (activeState === 'speaking' ? 0.44 : 0.76) : 0;
      const target = Object.fromEntries(Object.keys(stateTarget).map((key) => [
        key,
        mix(stateTarget[key], STATE_TARGETS.muted[key], mutedInfluence),
      ]));
      const resolvedInputEnergy = propsRef.current.explicitInputEnergy == null
        ? propsRef.current.busInputEnergy
        : propsRef.current.explicitInputEnergy;
      // LISTENING follows the microphone; SPEAKING follows Sonny's actual
      // playback. Keeping those ownership domains separate prevents room
      // noise or an interruption candidate from turning playback into a
      // visually different speaking mode.
      const outputEnergy = activeState === 'speaking'
        ? clamp(propsRef.current.busOutputEnergy) * 0.72
        : 0;
      const inputSpeechEnergy = activeState === 'listening' && !microphoneMuted
        ? speechGatedInputEnergy({
          speechActive: propsRef.current.speechActive,
          rawEnergy: resolvedInputEnergy,
        })
        : 0;
      const rawEnergy = activeState === 'speaking' ? outputEnergy : inputSpeechEnergy;
      const response = 1 - Math.exp(-elapsed * (activeState === 'listening' ? 5.8 : 3.8));
      speechBoundary = advanceSpeechBoundary({
        rawEnergy,
        elapsed,
        active: speechBoundary.active,
        gateCharge: speechBoundary.gateCharge,
        envelope: speechBoundary.envelope,
      });
      const energy = reduceMotion ? speechBoundary.envelope * 0.18 : speechBoundary.envelope;
      // Audio changes the material subtly and controls ring intensity. The
      // restrained geometry factor prevents a state handoff from presenting
      // a newly angled or differently shaped orb.
      const geometryEnergy = energy * 0.28;
      const orbHost = layoutHost?.closest?.('.br-voice-orb-wrap');
      orbHost?.style.setProperty('--br-orb-audio-energy', energy.toFixed(3));

      canvas.dataset.speechActive = speechBoundary.active ? 'true' : 'false';
      canvas.dataset.speechEnvelope = energy.toFixed(3);
      canvas.dataset.rawInputEnergy = clamp(rawEnergy).toFixed(3);
      if (process.env.NODE_ENV === 'development'
        && window.__BR_ORB_DIAGNOSTICS__ === true
        && now - lastDiagnosticTime >= 300) {
        lastDiagnosticTime = now;
        console.debug('[BrightResolve orb boundary]', {
          raw: Number(clamp(rawEnergy).toFixed(3)),
          smoothed: Number(speechBoundary.normalized.toFixed(3)),
          thresholdActive: speechBoundary.active,
          amplitude: Number(energy.toFixed(3)),
        });
      }

      Object.keys(current).forEach((key) => {
        current[key] = mix(current[key], target[key], response);
      });

      // Reassert the complete transform every frame. iOS can reset a canvas
      // backing store during viewport/chrome changes without delivering a
      // synchronous ResizeObserver callback. Drawing from an old transform is
      // what allowed the center to walk toward the upper-left after repeated
      // voice turns.
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      context.clearRect(0, 0, width, height);
      const cx = width / 2;
      const cy = height / 2;
      const baseRadius = Math.min(width, height) * 0.255;
      const radius = baseRadius;
      const materialExtent = radius * 1.08;
      const warmth = current.warmth;
      const amber = { r: mix(171, 244, warmth), g: mix(218, 224, warmth), b: mix(219, 174, warmth) };
      const aqua = { r: 109, g: 214, b: 202 };

      context.save();
      context.globalCompositeOperation = 'screen';

      if (!reduceMotion) {
        motes.forEach((mote, index) => {
          const angle = mote.angle + seconds * mote.speed * (0.35 + current.orbit);
          const distance = radius * (0.34 + mote.radius * 1.22);
          const pulse = 0.55 + Math.sin(seconds * 0.8 + index) * 0.25;
          context.fillStyle = rgba(
            index % 4 === 0 ? aqua.r : amber.r,
            index % 4 === 0 ? aqua.g : amber.g,
            index % 4 === 0 ? aqua.b : amber.b,
            (0.025 + current.presence * 0.07) * pulse * mote.depth,
          );
          context.beginPath();
          context.arc(cx + Math.cos(angle) * distance, cy + Math.sin(angle) * distance * 0.78, mote.size, 0, TAU);
          context.fill();
        });
      }
      context.restore();

      context.save();
      // One shared path is both the teal material edge and its only visible
      // contour. At zero speech envelope this resolves to an exact circle.
      traceOrbMaterialBoundary(context, cx, cy, radius, seconds, geometryEnergy, 0.7);
      context.clip();

      // A quiet teal body provides material color without painting a fixed
      // white spotlight into the center. The living volume below now owns the
      // visible illumination hierarchy.
      const body = context.createRadialGradient(cx, cy, radius * 0.08, cx, cy, radius * 1.12);
      body.addColorStop(0, rgba(87, 153, 153, 0.085 + current.presence * 0.026));
      body.addColorStop(0.34, rgba(63, 131, 139, 0.115 + current.emission * 0.018));
      body.addColorStop(0.62, rgba(30, 91, 105, 0.155 + current.presence * 0.025));
      body.addColorStop(0.82, 'rgba(12,58,69,0.205)');
      body.addColorStop(1, 'rgba(3,25,31,0.245)');
      context.fillStyle = body;
      context.fillRect(cx - materialExtent, cy - materialExtent, materialExtent * 2, materialExtent * 2);

      // Keep the center alive with a few broad, slowly drifting light fields.
      // This is intentionally constant-time: the previous per-pixel 3D-noise
      // pass monopolized Safari's UI thread and could expose a cleared canvas
      // between compositing frames. All light stays clipped inside the stable
      // circular body and remains independent from microphone energy.
      const driftX = reduceMotion ? 0 : Math.sin(seconds * 0.19 + 0.6) * radius * 0.09;
      const driftY = reduceMotion ? 0 : Math.cos(seconds * 0.16 - 0.4) * radius * 0.075;
      const livingLight = context.createRadialGradient(
        cx + driftX, cy + driftY, radius * 0.04,
        cx + driftX, cy + driftY, radius * 0.96,
      );
      livingLight.addColorStop(0, rgba(238, 246, 224, 0.11 + current.presence * 0.045));
      livingLight.addColorStop(0.38, rgba(aqua.r, aqua.g, aqua.b, 0.075 + current.focus * 0.025));
      livingLight.addColorStop(0.72, rgba(amber.r, amber.g, amber.b, 0.04 + current.emission * 0.018));
      livingLight.addColorStop(1, 'rgba(3,25,31,0)');
      context.globalCompositeOperation = 'screen';
      context.fillStyle = livingLight;
      context.fillRect(cx - materialExtent, cy - materialExtent, materialExtent * 2, materialExtent * 2);

      const counterLight = context.createRadialGradient(
        cx - driftX * 0.7, cy - driftY * 0.8, radius * 0.08,
        cx - driftX * 0.7, cy - driftY * 0.8, radius * 0.72,
      );
      counterLight.addColorStop(0, rgba(62, 167, 174, 0.045 + current.orbit * 0.02));
      counterLight.addColorStop(0.58, rgba(255, 232, 184, 0.025 + current.warmth * 0.012));
      counterLight.addColorStop(1, 'rgba(3,25,31,0)');
      context.fillStyle = counterLight;
      context.fillRect(cx - materialExtent, cy - materialExtent, materialExtent * 2, materialExtent * 2);

      context.restore();

      context.save();
      const renderedContourDisplacement = traceOrbMaterialBoundary(
        context,
        cx,
        cy,
        radius,
        seconds,
        geometryEnergy,
        0.7,
      );
      context.strokeStyle = rgba(7, 48, 57, 0.32 + energy * 0.08);
      context.lineWidth = 1.35 + energy * 0.32;
      context.stroke();
      context.restore();

      const previousRenderWitness = speechRenderWitness;
      speechRenderWitness = advanceSpeechRenderWitness({
        ...speechRenderWitness,
        reactiveGeometryRendered: (
          renderedContourDisplacement * dpr
          >= MIN_VISIBLE_CONTOUR_DISPLACEMENT_DEVICE_PX
        ),
      });
      if (
        canvas.dataset.speechRenderCycle == null
        || speechRenderWitness.cycle !== previousRenderWitness.cycle
        || speechRenderWitness.settledCycle !== previousRenderWitness.settledCycle
      ) {
        canvas.dataset.speechRenderCycle = String(speechRenderWitness.cycle);
        canvas.dataset.speechRenderSettledCycle = String(speechRenderWitness.settledCycle);
      }

      if (!canvasReady) {
        canvasReady = true;
        layoutHost?.setAttribute('data-canvas-ready', 'true');
      }

      frame = frameScheduler.request(render);
    };

    frame = frameScheduler.request(render);
    return () => {
      if (frame) frameScheduler.cancel(frame);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', resize);
      window.removeEventListener('orientationchange', resize);
      window.removeEventListener('pageshow', resize);
      window.visualViewport?.removeEventListener?.('resize', resize);
      motionQuery?.removeEventListener?.('change', updateMotionPreference);
      layoutHost?.closest?.('.br-voice-orb-wrap')?.style.removeProperty('--br-orb-audio-energy');
      layoutHost?.removeAttribute('data-canvas-ready');
    };
  }, []);

  return <canvas ref={canvasRef} className="br-voice-orb-canvas" data-testid="live-voice-orb" data-orb-state={state} data-mic-muted={micMuted ? 'true' : 'false'} aria-hidden="true" />;
}
